import { format } from 'date-fns';

import { MedicareBridgeResponseValue } from './medicare-bridge.types';

export const BRIDGE_DATE_OF_BIRTH_KEY = 'date_of_birth';
export const BRIDGE_THERAPY_COURSE_KEY = 'therapy_course';
export const BRIDGE_THERAPY_START_KEY = 'therapy_start_date';
export const BRIDGE_INITIAL_BMI_KEY = 'initial_bmi';
export const BRIDGE_INITIAL_BMI_EVIDENCE_KEY = 'initial_bmi_evidence';

export interface BridgeMeasurement {
  /** Pounds for weight, inches for height. */
  value: number;
  /** Calendar date YYYY-MM-DD. Null on an undated Healthie profile value. */
  date: string | null;
  source: 'healthie-entry' | 'healthie-profile';
}

export interface BridgeClinicalPrefill {
  dateOfBirth: string | null;
  profileWeightLbs: number | null;
  profileHeightInches: number | null;
  weights: BridgeMeasurement[];
  heights: BridgeMeasurement[];
}

export interface BmiEvidence {
  bmi: number;
  weight: BridgeMeasurement;
  height: BridgeMeasurement;
}

export type BmiSelection =
  | { status: 'waiting' }
  | { status: 'ready'; evidence: BmiEvidence }
  | { status: 'missing'; message: string };

/** The provider's calendar date. Never later than the platform's UTC date in US time zones. */
export function localToday(now = new Date()): string {
  return format(now, 'yyyy-MM-dd');
}

export function completedYears(dob: string, on: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) || !/^\d{4}-\d{2}-\d{2}$/.test(on)) {
    return null;
  }
  const [year, month, day] = dob.split('-').map(Number);
  const [onYear, onMonth, onDay] = on.split('-').map(Number);
  if (
    year == null ||
    month == null ||
    day == null ||
    onYear == null ||
    onMonth == null ||
    onDay == null
  ) {
    return null;
  }
  let age = onYear - year;
  if (onMonth < month || (onMonth === month && onDay < day)) age -= 1;
  return age;
}

export function bmiFromMeasurements(
  weightLbs: number,
  heightInches: number,
): number | null {
  if (
    !Number.isFinite(weightLbs) ||
    !Number.isFinite(heightInches) ||
    weightLbs <= 0 ||
    heightInches <= 0
  ) {
    return null;
  }
  return Math.round(((weightLbs / (heightInches * heightInches)) * 703) * 10) / 10;
}

/**
 * New starts use the latest Healthie weight and height on or before the start
 * date, including the profile when no dated entry exists. Continuing therapy
 * uses the latest entry on or before the start date and never a later or
 * undated measurement.
 */
export function selectBmiEvidence(input: {
  prefill: BridgeClinicalPrefill | undefined;
  course: MedicareBridgeResponseValue | undefined;
  therapyStartDate: string | null;
}): BmiSelection {
  if (!input.prefill) return { status: 'waiting' };
  if (input.course === 'NEW_START') {
    const weight =
      latest(input.prefill.weights, input.therapyStartDate) ??
      profileWeight(input.prefill);
    const height =
      latest(input.prefill.heights, input.therapyStartDate) ??
      profileHeight(input.prefill);
    return pair(weight, height, 'Healthie has no weight and height for this new start. Enter the BMI.');
  }
  if (input.course === 'CONTINUING') {
    if (!input.therapyStartDate) {
      return {
        status: 'missing',
        message: 'Enter the therapy start date to use the BMI from initiation.',
      };
    }
    const weight = latest(input.prefill.weights, input.therapyStartDate);
    const height = latest(input.prefill.heights, input.therapyStartDate);
    return pair(
      weight,
      height,
      'No Healthie weight and height on or before the therapy start date. Enter the BMI from initiation.',
    );
  }
  return { status: 'waiting' };
}

/** Where the submitted BMI came from, saved with the review. Null when there is nothing to explain. */
export function bmiEvidence(input: {
  selection: BmiSelection;
  edited: boolean;
  fromPreviousReview: boolean;
  savedEvidence: string | null;
}): string | null {
  if (!input.edited && input.fromPreviousReview) {
    return input.savedEvidence ?? 'From the previous review.';
  }
  if (input.selection.status === 'ready') {
    const source = measurementLabel(input.selection.evidence);
    return input.edited ? `Corrected from ${source}.` : source;
  }
  return input.edited ? 'Entered for this review.' : null;
}

export function bmiSourceLabel(
  selection: BmiSelection,
  edited: boolean,
  fromPreviousReview = false,
  savedEvidence: string | null = null,
): string | null {
  const reused = !edited && fromPreviousReview;
  if (selection.status === 'waiting' && !reused) return null;
  if (selection.status === 'missing' && !edited && !reused) {
    return selection.message;
  }
  if (!edited && !reused && selection.status === 'ready') {
    const line = sameDayMeasurementLine(selection.evidence);
    if (line) return line;
  }
  return bmiEvidence({ selection, edited, fromPreviousReview, savedEvidence });
}

export type DobPresentation =
  | { kind: 'age'; age: number }
  | { kind: 'message'; text: string };

/** Compact age row when Healthie has a date of birth. Otherwise an actionable message. */
export function dobPresentation(input: {
  loaded: boolean;
  failed: boolean;
  dateOfBirth: string | null | undefined;
  savedDateOfBirth?: string | null;
  today: string;
  /** Completed years already derived for the intake stop. */
  age: number | null;
}): DobPresentation {
  if (
    input.loaded &&
    !input.failed &&
    input.dateOfBirth &&
    input.age != null &&
    formatIsoDate(input.dateOfBirth)
  ) {
    return { kind: 'age', age: input.age };
  }
  return { kind: 'message', text: dobStatusLabel(input) };
}

export function dobStatusLabel(input: {
  loaded: boolean;
  failed: boolean;
  dateOfBirth: string | null | undefined;
  savedDateOfBirth?: string | null;
  today: string;
  age: number | null;
}): string {
  if (!input.loaded && !input.failed) return 'Checking Healthie for a date of birth.';
  if (input.failed) {
    const formatted = input.savedDateOfBirth
      ? formatIsoDate(input.savedDateOfBirth)
      : null;
    if (input.age == null || !formatted) {
      return 'Healthie date of birth could not be loaded.';
    }
    return `Age ${input.age}, from the previous review's date of birth (${formatted}). Healthie date of birth could not be loaded.`;
  }
  if (!input.dateOfBirth) return 'Date of birth is missing in Healthie.';
  const age = completedYears(input.dateOfBirth, input.today);
  const formatted = formatIsoDate(input.dateOfBirth);
  if (age == null || !formatted) return 'Date of birth is missing in Healthie.';
  return `Age ${age}, from Healthie date of birth (${formatted}).`;
}

export function showTherapyStartInput(
  course: MedicareBridgeResponseValue | undefined,
  documentedDate: string | null,
  correcting: boolean,
): boolean {
  if (course !== 'CONTINUING') return false;
  if (correcting) return true;
  return documentedDate == null;
}

export function therapyStartSourceLabel(input: {
  course: MedicareBridgeResponseValue | undefined;
  documentedDate: string | null;
  correcting: boolean;
  newStartDate: string;
}): string | null {
  if (input.course === 'NEW_START') {
    return `Treatment started ${input.newStartDate}`;
  }
  if (
    input.course === 'CONTINUING' &&
    input.documentedDate &&
    !input.correcting
  ) {
    return `Treatment started ${input.documentedDate}`;
  }
  return null;
}

function sameDayMeasurementLine(evidence: BmiEvidence): string | null {
  const date = evidence.weight.date;
  if (!date || evidence.height.date !== date) return null;
  const formatted = formatIsoDate(date);
  return formatted ? `Healthie measurement · ${formatted}` : null;
}

function pair(
  weight: BridgeMeasurement | null,
  height: BridgeMeasurement | null,
  missing: string,
): BmiSelection {
  if (!weight || !height) return { status: 'missing', message: missing };
  const bmi = bmiFromMeasurements(weight.value, height.value);
  if (bmi == null) return { status: 'missing', message: missing };
  return { status: 'ready', evidence: { bmi, weight, height } };
}

function profileWeight(prefill: BridgeClinicalPrefill): BridgeMeasurement | null {
  if (prefill.profileWeightLbs == null || prefill.profileWeightLbs <= 0) return null;
  return {
    value: prefill.profileWeightLbs,
    date: null,
    source: 'healthie-profile',
  };
}

function profileHeight(prefill: BridgeClinicalPrefill): BridgeMeasurement | null {
  if (prefill.profileHeightInches == null || prefill.profileHeightInches <= 0) {
    return null;
  }
  return {
    value: prefill.profileHeightInches,
    date: null,
    source: 'healthie-profile',
  };
}

function latest(
  measurements: BridgeMeasurement[],
  onOrBefore: string | null,
): BridgeMeasurement | null {
  const dated = measurements.filter(
    (measurement): measurement is BridgeMeasurement & { date: string } =>
      measurement.date != null &&
      (onOrBefore == null || measurement.date <= onOrBefore),
  );
  dated.sort((left, right) => right.date.localeCompare(left.date));
  return dated[0] ?? null;
}

function measurementLabel(evidence: BmiEvidence): string {
  if (
    evidence.weight.source === 'healthie-profile' &&
    evidence.height.source === 'healthie-profile'
  ) {
    return 'Healthie profile weight and height, with no measurement date';
  }
  return `Healthie weight ${measurementWhen(evidence.weight)} and Healthie height ${measurementWhen(evidence.height)}`;
}

function measurementWhen(measurement: BridgeMeasurement): string {
  if (measurement.date) {
    return `on ${formatIsoDate(measurement.date) ?? measurement.date}`;
  }
  return 'on the Healthie profile, with no measurement date';
}

function formatIsoDate(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (year == null || month == null || day == null) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
